/**
 * PATH: packages/ui/src/hooks/useTabNavigation.ts
 * PURPOSE: Parameterized hook to sync browser-style tab state with React Router.
 *
 * WHY: Every hub needs the same tab ↔ route sync logic. This hook extracts
 *      the CRM-proven pattern into a generic, configurable form.
 *
 * USAGE:
 *   const tabNav = useTabNavigation({
 *     useStore: useWorkforceTabStore,
 *     routeMap: { '/people': { title: 'People', iconKey: 'Users' } },
 *     iconMap: { Users },
 *     defaultNewTabPath: '/people',
 *   })
 *
 * DEPENDENCIES:
 *  - react-router-dom: useLocation, useNavigate
 *  - Tab store created via createTabStore
 */

import { useEffect, useCallback, useMemo, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import type { ComponentType } from 'react'
import type { TabItem, TabCloseOptions } from '../components/TabBar'
import type { Tab, TabStore } from '../stores/createTabStore'
import { useHub } from '../contexts/HubContext'

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

export interface RouteMetadata {
  title: string
  iconKey: string
}

export interface DynamicRoute {
  /** RegExp pattern to match (e.g. /^\/people\/(.+)/) */
  pattern: RegExp
  /** Extract metadata from a matched path */
  getMetadata: (path: string) => RouteMetadata
}

export interface TabNavigationConfig {
  /** Zustand tab store hook (from createTabStore) */
  useStore: ReturnType<typeof import('../stores/createTabStore').createTabStore>
  /** Static route → metadata mapping */
  routeMap: Record<string, RouteMetadata>
  /** Dynamic route patterns (e.g. /quotes/:id, /people/:id) */
  dynamicRoutes?: DynamicRoute[]
  /** Paths that should never create tabs (SSO, login, settings, etc.) */
  excludedPaths?: string[]
  /**
   * Like ``excludedPaths`` but matched as regex. Use this when you need
   * fine-grained exclusion that the prefix-based ``excludedPaths`` can't
   * express — e.g. exclude per-detail routes (``/quotes/builder/<id>``)
   * while STILL letting the bare hub page (``/quotes/builder``) own a
   * global tab. Each pattern is tested with ``RegExp.prototype.test``.
   */
  excludedPatterns?: RegExp[]
  /** Path opened by the "+" new tab button (defaults to first route in routeMap) */
  defaultNewTabPath?: string
  /** Icon key → component map for serialization/deserialization */
  iconMap: Record<string, ComponentType<{ className?: string }>>
  /** Fallback icon key when route has no match */
  fallbackIconKey?: string
  /**
   * Optional pre-close gate. Receives the tab being closed plus the
   * modifier-key info from the close click. Return `false` to abort the
   * close (no state change, no navigation). Used by SAM to gate the
   * close of a draft (`isDraft`) tab behind a confirm dialog with
   * Shift-to-suppress.
   */
  onBeforeClose?: (tab: Tab, options?: TabCloseOptions) => boolean
}

export interface TabNavigationReturn {
  tabItems: TabItem[]
  activeTabId: string | null
  handleTabClick: (id: string) => void
  handleTabClose: (id: string, options?: TabCloseOptions) => void
  handleNewTab: () => void
  handleTabPin: (id: string) => void
  handleCloseOthers: (id: string) => void
  handleDuplicateTab: (id: string) => void
  openInNewTab: (path: string) => void
}

// ─────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────

export function useTabNavigation(config: TabNavigationConfig): TabNavigationReturn {
  const {
    useStore,
    routeMap,
    dynamicRoutes = [],
    excludedPaths = [],
    excludedPatterns = [],
    defaultNewTabPath,
    iconMap,
    fallbackIconKey = 'FileText',
    onBeforeClose,
  } = config

  const location = useLocation()
  const rawNavigate = useNavigate()
  const { basePath } = useHub()
  const isNavigatingFromTab = useRef(false)
  const lastSyncedPath = useRef<string | null>(null)

  // ── Hub-aware path helpers ────────────────────────────
  // When running in a hub context (basePath = '/workforce'), strip the
  // prefix for route map matching and prepend it when navigating.
  const toHubRelative = useCallback((fullPath: string): string => {
    if (basePath && fullPath.startsWith(basePath)) {
      return fullPath.slice(basePath.length) || '/'
    }
    return fullPath
  }, [basePath])

  const navigate = useCallback((path: string) => {
    const fullPath = basePath && !path.startsWith(basePath) ? `${basePath}${path}` : path
    rawNavigate(fullPath)
  }, [rawNavigate, basePath])

  const {
    tabs,
    activeTabId,
    openTab,
    closeTab,
    setActiveTab,
    togglePin,
    closeOthers,
    duplicateTab,
    updateActiveTab,
  } = useStore()

  // ── Excluded paths set (stable) ─────────────────────────
  const excludedSet = useMemo(() => new Set(excludedPaths), [excludedPaths])

  function isExcludedPath(path: string): boolean {
    if (excludedSet.has(path)) return true
    for (const excluded of excludedSet) {
      if (path.startsWith(excluded + '/')) return true
    }
    for (const pattern of excludedPatterns) {
      if (pattern.test(path)) return true
    }
    return false
  }

  // ── Route → metadata resolver ───────────────────────────
  function getRouteMetadata(path: string): RouteMetadata {
    // Exact match from static map
    if (routeMap[path]) return routeMap[path]

    // Try dynamic route patterns
    for (const dr of dynamicRoutes) {
      if (dr.pattern.test(path)) {
        return dr.getMetadata(path)
      }
    }

    // Fallback: use path segment as title
    return {
      title: path.split('/').filter(Boolean).pop() || 'Page',
      iconKey: fallbackIconKey,
    }
  }

  // ── Icon resolver ───────────────────────────────────────
  function resolveIcon(iconKey?: string): ComponentType<{ className?: string }> | undefined {
    if (!iconKey) return undefined
    return iconMap[iconKey]
  }

  // ── Default new tab path ────────────────────────────────
  const newTabPath = defaultNewTabPath ?? Object.keys(routeMap)[0] ?? '/'

  // ── Sync route → tabs ──────────────────────────────────
  useEffect(() => {
    if (isNavigatingFromTab.current) {
      isNavigatingFromTab.current = false
      return
    }

    // Use hub-relative path for route map matching, full path for tab storage
    const hubRelativePath = toHubRelative(location.pathname)
    const currentPath = hubRelativePath
    if (!currentPath || currentPath === '/') return
    if (isExcludedPath(currentPath)) return
    if (lastSyncedPath.current === currentPath) return
    lastSyncedPath.current = currentPath

    // 1. Exact tab match → activate
    const existingTab = tabs.find(t => t.path === currentPath)
    if (existingTab) {
      if (existingTab.id !== activeTabId) {
        setActiveTab(existingTab.id)
      }
      return
    }

    // 2. No exact match — figure out where to put this route
    const meta = getRouteMetadata(currentPath)
    const activeTab = tabs.find(t => t.id === activeTabId)
    const currentSection = currentPath.split('/').filter(Boolean)[0]

    if (!activeTab) {
      openTab(currentPath, meta.title, meta.iconKey)
      return
    }

    if (!activeTab.isPinned) {
      // Active tab is NOT pinned — update in-place (browser-like)
      updateActiveTab(currentPath, meta.title, meta.iconKey)
      return
    }

    // Active tab IS pinned — never mutate it
    const activeSection = activeTab.path.split('/').filter(Boolean)[0]
    const isSameSection = activeSection === currentSection

    if (isSameSection) {
      // Same section: reuse the most recent unpinned tab in this section
      const sectionTab = [...tabs]
        .reverse()
        .find(t => !t.isPinned && t.path.split('/').filter(Boolean)[0] === currentSection)

      if (sectionTab) {
        // Update existing section tab and activate it
        const store = useStore.getState()
        const updatedTabs = store.tabs.map(t =>
          t.id === sectionTab.id
            ? { ...t, path: currentPath, title: meta.title, ...(meta.iconKey && { iconKey: meta.iconKey }) }
            : t
        )
        useStore.setState({ tabs: updatedTabs, activeTabId: sectionTab.id })
      } else {
        openTab(currentPath, meta.title, meta.iconKey)
      }
    } else {
      // Different section — always open a new tab
      openTab(currentPath, meta.title, meta.iconKey)
    }
  }, [location.pathname, toHubRelative]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Tab click → route ──────────────────────────────────
  const handleTabClick = useCallback((tabId: string) => {
    const tab = tabs.find(t => t.id === tabId)
    if (!tab) return

    isNavigatingFromTab.current = true
    lastSyncedPath.current = tab.path
    setActiveTab(tabId)
    navigate(tab.path)
  }, [tabs, setActiveTab, navigate])

  // ── Close tab ──────────────────────────────────────────
  const handleTabClose = useCallback((tabId: string, options?: TabCloseOptions) => {
    const store = useStore.getState()
    const tab = store.tabs.find(t => t.id === tabId)
    if (!tab) return
    if (onBeforeClose && !onBeforeClose(tab, options)) return
    const closingActive = tabId === store.activeTabId
    const tabIndex = store.tabs.findIndex(t => t.id === tabId)

    closeTab(tabId)

    if (closingActive) {
      const nextTab = store.tabs[tabIndex + 1] || store.tabs[tabIndex - 1]
      if (nextTab) {
        isNavigatingFromTab.current = true
        lastSyncedPath.current = nextTab.path
        navigate(nextTab.path)
      }
    }
  }, [closeTab, navigate, onBeforeClose, useStore])

  // ── New tab ────────────────────────────────────────────
  const handleNewTab = useCallback(() => {
    const meta = getRouteMetadata(newTabPath)
    openTab(newTabPath, meta.title, meta.iconKey)
    isNavigatingFromTab.current = true
    lastSyncedPath.current = newTabPath
    navigate(newTabPath)
  }, [openTab, navigate, newTabPath]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Open specific path in new tab ──────────────────────
  const openInNewTab = useCallback((path: string) => {
    const meta = getRouteMetadata(path)
    openTab(path, meta.title, meta.iconKey)
    isNavigatingFromTab.current = true
    lastSyncedPath.current = path
    navigate(path)
  }, [openTab, navigate]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Build TabItem[] for TabBar ─────────────────────────
  const tabItems: TabItem[] = useMemo(() =>
    tabs.map(tab => ({
      id: tab.id,
      path: tab.path,
      title: tab.title,
      icon: resolveIcon(tab.iconKey),
      isPinned: tab.isPinned,
      isDirty: tab.isDirty,
      isDraft: tab.isDraft,
    })),
    [tabs] // eslint-disable-line react-hooks/exhaustive-deps
  )

  return {
    tabItems,
    activeTabId,
    handleTabClick,
    handleTabClose,
    handleNewTab,
    handleTabPin: togglePin,
    handleCloseOthers: closeOthers,
    handleDuplicateTab: duplicateTab,
    openInNewTab,
  }
}

export default useTabNavigation
